<template>
  <div class="home-page">
    <HpsfirProPageHeader
      title="首页"
      title-en="DASHBOARD"
      description="查看设备、扫码和站内信概览"
      :top-operates="headerActions"
      :max-visible-actions="4"
    />

    <div class="home-page__content">
      <HpsfirProStatsCards
        class="home-page__stats"
        :items="statCards"
        v-loading="statisticLoading"
      />

      <section class="home-page__message-card" v-loading="messageLoading">
        <div class="home-page__message-header">
          <div>
            <div class="home-page__message-title">站内信</div>
            <div class="home-page__message-desc">最近 {{ messages.length }} 条站内通知</div>
          </div>
        </div>

        <div v-if="!messages.length" class="home-page__empty">
          <div class="home-page__empty-icon">MSG</div>
          <div class="home-page__empty-title">暂无站内信</div>
          <div class="home-page__empty-desc">新的通知会显示在这里。</div>
        </div>
        <div v-else class="home-page__message-list">
          <article class="home-page__message-item" v-for="msg in messages" :key="msg.id">
            <div class="home-page__message-item-main">
              <time class="home-page__message-time">{{ dayjs(msg.createTime).format('YYYY/MM/DD') }}</time>
              <div class="home-page__message-content">{{ msg.msg }}</div>
            </div>
            <HpsfirButton
              v-if="canDeleteMessage"
              variant="destructive"
              size="sm"
              @click="() => handleDeleteMsg(msg.id)"
            >删除</HpsfirButton>
          </article>
        </div>
      </section>
    </div>

    <HpsfirProForm :is-dialog="true" ref="sendMsgForm" title="发送站内信" :items="messageItems" @ok="handleSendMsg" />
    <HpsfirProForm :is-dialog="true" ref="deleteUserForm" title="删除用户" :items="deleteUserItems" @ok="handleDeleteUserFormOk" />
    <HpsfirProForm :is-dialog="true" ref="clearLoginInfoForm" title="清理用户登录信息" :items="clearLoginInfoItems" @ok="handleClearLoginOk" />
    <HpsfirProDangerDialog
      ref="deleteMessageDialogRef"
      title="删除站内信"
      opt-name="删除"
      :on-ok="deleteAction"
      :format-dangder-delete-info="formatDeleteMessageInfo"
    />
    <HpsfirProDangerDialog
      ref="deleteUserDialogRef"
      title="删除用户"
      opt-name="删除用户"
      :on-ok="deleteUserAction"
      :format-dangder-delete-info="formatDeleteUserInfo"
    />
  </div>
</template>
<script lang="ts" setup>
import {
  useBaseOperateStore,
  type HpsfirProFormItem,
  type HpsfirProStatsCardItem,
} from 'hpsfir-managebase-ui';

import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import { Activity, MessageSquare, QrCode, Router } from 'lucide-vue-next';

const baseOperateStore = useBaseOperateStore();

const statistics = ref({
  onlineDevice: 0,
  totalCount: 0,
  totalDevice: 0,
  totalPeople: 0,
});

const messages = ref<any[]>([]);

const canSendMessage = computed(() => baseOperateStore.operates?.some((item) => item.key === 'SEND_MESSAGE'));
const canDeleteMessage = computed(() => baseOperateStore.operates?.some((item) => item.key === 'DEL_MESSAGE'));
const canDeleteUser = computed(() => baseOperateStore.operates?.some((item) => item.key === 'DEL_USER'));
const canClearUserLoginInfo = computed(() => baseOperateStore.operates?.some((item) => item.key === 'CLEAR_USER_LOGIN_INFO'));

const statCards = computed<HpsfirProStatsCardItem[]>(() => [
  {
    label: '累计设备数量',
    value: statistics.value.totalDevice,
    icon: Router,
    color: 'blue',
    subText: '全部已绑定设备',
  },
  {
    label: '在线设备',
    value: statistics.value.onlineDevice,
    icon: Activity,
    color: 'green',
    subText: '当前在线设备',
  },
  {
    label: '累计扫码次数',
    value: statistics.value.totalCount,
    icon: QrCode,
    color: 'purple',
    subText: '历史扫码总次数',
  },
  {
    label: '累计扫码人数',
    value: statistics.value.totalPeople,
    icon: MessageSquare,
    color: 'orange',
    subText: '历史扫码用户数',
  },
]);

const messageItems = ref<HpsfirProFormItem[]>([
  {
    name: 'msg',
    label: '内容',
    rule: [{ required: true }],
    type: 'textaera',
  },
]);

const deleteUserItems = ref<HpsfirProFormItem[]>([
  {
    name: 'phone',
    label: '手机号',
    rule: [
      { required: true, message: '请输入手机号' },
      { pattern: /^1\d{10}$/u, message: '请输入正确的手机号' },
    ],
    type: 'string',
  },
]);

const clearLoginInfoItems = ref<HpsfirProFormItem[]>([
  {
    name: 'count',
    label: '清理数量',
    rule: [
      { required: true, message: '请输入清理数量' },
      { type: 'number', min: 1, message: '清理数量必须大于0' },
    ],
    type: 'number',
  },
]);

const sendMsgForm = ref<any>();
const deleteUserForm = ref<any>();
const clearLoginInfoForm = ref<any>();
const deleteMessageDialogRef = ref<any>();
const deleteUserDialogRef = ref<any>();

const statisticLoading = ref(false);

const getStatistics = async() => {
  try {
    statisticLoading.value = true;
    const res = await useApi().homeApi.getHomeStatisticshome();
    statistics.value = res.data.data || statistics.value;
  } finally {
    statisticLoading.value = false;
  }
};

const messageLoading = ref(false);
const getMessage = async() => {
  try {
    messageLoading.value = true;
    const res = await useApi().homeApi.listMessagehome({ conditions: {} });
    messages.value = res.data.data || [];
  } finally {
    messageLoading.value = false;
  }
};

function refreash() {
  getStatistics();
  getMessage();
}

const handleSendMsg = async(row: any) => {
  messageLoading.value = true;
  await useApi().homeApi.addMessagehome({ messageAdminVo: row });
  ElMessage.success('发送成功');
  refreash();
};

const formatDeleteMessageInfo = (row: any) => `您正在进行删除站内信(${row?.id})操作，请谨慎操作`;

const deleteAction = async(row: any) => {
  await useApi().homeApi.delMessagehome(row.id);
  ElMessage.success('删除成功');
  refreash();
};

const handleDeleteMsg = (msgId: number) => {
  deleteMessageDialogRef.value?.showDialog({ id: msgId });
};

const formatDeleteUserInfo = (row: any) => `您正在删除手机号(${row?.phone})的基础用户、关联店铺、开门数据等业务数据，此操作不可恢复，请谨慎操作`;

const deleteUserAction = async(row: any) => {
  const phone = String(row?.phone || '').trim();
  const res = await useApi().homeApi.delUserByPhonehome({ phone });
  const storeCount = res.data.data?.storeCount || 0;
  ElMessage.success(storeCount ? `删除成功，已清理${storeCount}个关联店铺` : '删除成功');
  refreash();
};

const handleDeleteUserFormOk = (row: any) => {
  const phone = String(row?.phone || '').trim();
  if (!/^1\d{10}$/u.test(phone)) {
    ElMessage.error('请输入正确的手机号');
    return;
  }
  deleteUserDialogRef.value?.showDialog({ phone });
};

const handleClearLoginOk = async(row: any) => {
  const count = Number(row?.count);
  if (!Number.isSafeInteger(count) || count <= 0) {
    ElMessage.error('请输入大于0的清理数量');
    return;
  }
  const res = await useApi().homeApi.clearUserLoginInfohome({ count });
  const deletedCount = res.data.data?.deletedCount || 0;
  const remainingUnexpiredCount = res.data.data?.remainingUnexpiredCount || 0;
  ElMessage.success(`清理完成，已清理${deletedCount}条登录信息，剩余未过期${remainingUnexpiredCount}条`);
};

function handleClickSend() {
  sendMsgForm.value?.reset();
  sendMsgForm.value?.showDialog();
}

function handleClickDeleteUser() {
  deleteUserForm.value?.reset();
  deleteUserForm.value?.showDialog();
}

function handleClickClearLoginInfo() {
  clearLoginInfoForm.value?.reset();
  clearLoginInfoForm.value?.showDialog();
}

const headerActions = computed(() => [
  {
    key: 'refresh',
    name: '刷新数据',
    handler: () => refreash(),
    variant: 'outline' as const,
  },
  ...(canSendMessage.value
    ? [
      {
        key: 'send',
        name: '发送站内信',
        handler: () => handleClickSend(),
      },
    ]
    : []),
  ...(canDeleteUser.value
    ? [
      {
        key: 'delete-user',
        name: '删除用户',
        handler: () => handleClickDeleteUser(),
        variant: 'destructive' as const,
      },
    ]
    : []),
  ...(canClearUserLoginInfo.value
    ? [
      {
        key: 'clear-user-login-info',
        name: '清理用户登录信息',
        handler: () => handleClickClearLoginInfo(),
        variant: 'destructive' as const,
      },
    ]
    : []),
]);

refreash();
</script>
<style lang="scss" scoped>
.home-page {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 16px;
  }

  &__stats {
    width: 100%;
  }

  &__message-card {
    overflow: hidden;
    min-height: 280px;
    border: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 76%, transparent);
    border-radius: 22px;
    background: var(--card, #fff);
    box-shadow: 0 18px 44px -34px rgba(15, 23, 42, 0.5);
  }

  &__message-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    border-bottom: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 70%, transparent);
    background: color-mix(in srgb, var(--muted, #f3f4f6) 34%, transparent);
  }

  &__message-title {
    font-size: 18px;
    font-weight: 800;
    color: var(--foreground, #111827);
  }

  &__message-desc {
    margin-top: 4px;
    color: var(--muted-foreground, #6b7280);
    font-size: 13px;
  }

  &__message-list {
    display: flex;
    flex-direction: column;
    padding: 8px 16px 16px;
  }

  &__message-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 4px;
    border-bottom: 1px solid color-mix(in srgb, var(--border, #e5e7eb) 60%, transparent);

    &:last-child {
      border-bottom: none;
    }
  }

  &__message-item-main {
    display: grid;
    min-width: 0;
    grid-template-columns: 120px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
  }

  &__message-time {
    color: var(--muted-foreground, #6b7280);
    font-size: 13px;
  }

  &__message-content {
    min-width: 0;
    color: var(--foreground, #111827);
    overflow-wrap: anywhere;
  }

  &__empty {
    display: flex;
    min-height: 200px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 28px;
    text-align: center;
  }

  &__empty-icon {
    display: flex;
    width: 52px;
    height: 52px;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    background: var(--primary, #111827);
    color: var(--primary-foreground, #fff);
    font-weight: 800;
    letter-spacing: 0.06em;
  }

  &__empty-title {
    font-weight: 800;
    color: var(--foreground, #111827);
  }

  &__empty-desc {
    color: var(--muted-foreground, #6b7280);
    font-size: 13px;
  }
}

@media (max-width: 767px) {
  .home-page {
    gap: 12px;

    &__content {
      gap: 12px;
      padding: 0 8px 12px;
    }


    &__message-card {
      border-radius: 18px;
    }

    &__message-header {
      align-items: stretch;
      flex-direction: column;
      padding: 16px;
    }

    &__message-list {
      padding: 4px 12px 12px;
    }

    &__message-item {
      align-items: stretch;
      flex-direction: column;
      gap: 10px;
      padding: 14px 0;
    }

    &__message-item-main {
      grid-template-columns: 1fr;
      gap: 6px;
    }
  }
}
</style>
